<script setup>
import { ref, computed } from 'vue';
import ForwardToOption from './emailChannels/ForwardToOption.vue';
import Microsoft from './emailChannels/Microsoft.vue';
import Google from './emailChannels/Google.vue';
import ChannelSelector from 'dashboard/components/ChannelSelector.vue';
import PageHeader from '../../SettingsSubPageHeader.vue';

import { useStoreGetters } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

const provider = ref('');

const getters = useStoreGetters();
const { t } = useI18n();
const route = useRoute();

// Set by the OAuth callback when Google returned a token without Gmail access.
const hasMissingGmailScope = computed(
  () => route.query.oauth_error === 'google_mail_scope_missing'
);

const globalConfig = getters['globalConfig/get'];
const isATalkviaInstance = getters['globalConfig/isATalkviaInstance'];

const emailProviderList = computed(() => {
  return [
    {
      title: t('INBOX_MGMT.EMAIL_PROVIDERS.MICROSOFT.TITLE'),
      description: t('INBOX_MGMT.EMAIL_PROVIDERS.MICROSOFT.DESCRIPTION'),
      isEnabled: Boolean(globalConfig.value?.azureAppId),
      key: 'microsoft',
      icon: 'i-brand-microsoft',
    },
    {
      title: t('INBOX_MGMT.EMAIL_PROVIDERS.GOOGLE.TITLE'),
      description: t('INBOX_MGMT.EMAIL_PROVIDERS.GOOGLE.DESCRIPTION'),
      isEnabled: Boolean(
        globalConfig.value?.googleOAuthClientId ||
          window.chatwootConfig?.googleOAuthClientId
      ),
      key: 'google',
      icon: 'i-brand-google',
    },
    {
      title: t('INBOX_MGMT.EMAIL_PROVIDERS.OTHER_PROVIDERS.TITLE'),
      description: t('INBOX_MGMT.EMAIL_PROVIDERS.OTHER_PROVIDERS.DESCRIPTION'),
      isEnabled: true,
      key: 'other_provider',
      icon: 'i-woot-mail',
    },
  ].filter(providerConfig => {
    if (isATalkviaInstance.value) {
      return true;
    }
    return providerConfig.isEnabled;
  });
});

function onClick(emailProvider) {
  if (emailProvider.isEnabled) {
    provider.value = emailProvider.key;
  }
}
</script>

<template>
  <div v-if="!provider" class="h-full w-full p-6 col-span-6">
    <PageHeader
      class="max-w-4xl"
      :header-title="$t('INBOX_MGMT.ADD.EMAIL_PROVIDER.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.EMAIL_PROVIDER.DESCRIPTION')"
    />
    <div
      v-if="hasMissingGmailScope"
      class="mt-5 flex max-w-3xl items-start gap-3 rounded-xl border border-n-ruby-6 bg-n-ruby-2 px-4 py-3"
      role="alert"
    >
      <span
        class="i-hugeicons-alert-02 mt-0.5 size-5 shrink-0 text-n-ruby-11"
        aria-hidden="true"
      />
      <div class="min-w-0">
        <p class="mb-1 text-sm font-semibold text-n-ruby-11">
          {{ $t('INBOX_MGMT.EMAIL_PROVIDERS.GOOGLE.SCOPE_MISSING_TITLE') }}
        </p>
        <p class="mb-0 text-sm leading-5 text-n-slate-12">
          {{ $t('INBOX_MGMT.EMAIL_PROVIDERS.GOOGLE.SCOPE_MISSING_BODY') }}
        </p>
      </div>
    </div>
    <div class="grid max-w-3xl grid-cols-4 gap-6 mx-0 mt-6">
      <ChannelSelector
        v-for="emailProvider in emailProviderList"
        :key="emailProvider.key"
        :title="emailProvider.title"
        :description="emailProvider.description"
        :icon="emailProvider.icon"
        :disabled="!emailProvider.isEnabled"
        @click="() => onClick(emailProvider)"
      />
    </div>
  </div>
  <Microsoft v-else-if="provider === 'microsoft'" />
  <Google v-else-if="provider === 'google'" />
  <ForwardToOption v-else-if="provider === 'other_provider'" />
</template>
